import { Badge } from '@mantine/core';
import { IconShoppingCart } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IPopulatedListing } from '../../types/Listing.model';
import { Listing } from '../../types/Listing.model';
import classes from './CardMetaBadge.module.css';

export type ListingStatusKey = 'listed' | 'pending' | 'notListed';

interface ListingStatusBadgeProps {
  listings?: IPopulatedListing[];
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showIcon?: boolean;
  style?: React.CSSProperties;
  appearance?: 'default' | 'card';
}

export function getListingStatus(
  listings?: IPopulatedListing[],
  t?: (key: string) => string
): { label: string; color: string; key: ListingStatusKey } {
  if (!listings || listings.length === 0) {
    return {
      label: t?.('dashboard.listing.notListed') || 'Not Listed',
      color: 'gray',
      key: 'notListed',
    };
  }

  const hasConfirmed = listings.some(
    (l) =>
      l.appConfirmation === Listing.Confirmation.CONFIRMED &&
      l.productConfirmation === Listing.Confirmation.CONFIRMED
  );
  const hasPending = listings.some(
    (l) =>
      l.appConfirmation === Listing.Confirmation.PENDING ||
      l.productConfirmation === Listing.Confirmation.PENDING
  );

  if (hasConfirmed) {
    return {
      label: t?.('dashboard.listing.listed') || 'Listed',
      color: 'green',
      key: 'listed',
    };
  }
  if (hasPending) {
    return {
      label: t?.('dashboard.listing.listingRequested') || 'Listing Requested',
      color: 'yellow',
      key: 'pending',
    };
  }
  return {
    label: t?.('dashboard.listing.notListed') || 'Not Listed',
    color: 'gray',
    key: 'notListed',
  };
}

export function ListingStatusBadge({
  listings,
  size = 'sm',
  showIcon = true,
  style,
  appearance = 'default',
}: ListingStatusBadgeProps) {
  const { t } = useTranslation();
  const status = getListingStatus(listings, t);

  if (appearance === 'card') {
    return (
      <span
        className={`${classes.badge} ${classes[status.key]}`}
        style={style}
        title={status.label}
      >
        {showIcon && <IconShoppingCart size={9} className={classes.icon} stroke={2} />}
        <span className={classes.label}>{status.label}</span>
      </span>
    );
  }

  return (
    <Badge color={status.color} variant="light" size={size} style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: showIcon ? '3px' : '0' }}>
        {showIcon && <IconShoppingCart size={size === 'lg' ? 14 : 10} style={{ flexShrink: 0 }} />}
        {status.label}
      </div>
    </Badge>
  );
}
